import React, { useState, useEffect } from 'react';
import { getHrisPelanggaran, getHrisPelanggaranById, createHrisPelanggaran, updateHrisPelanggaran, deleteHrisPelanggaran, getKaryawan } from '../../api';
import { Plus, Edit2, Trash2, AlertTriangle, Search, Calendar, Shield, Clock } from 'lucide-react';

const JENIS_PELANGGARAN = ['Ringan', 'Sedang', 'Berat'];
const JENIS_SANKSI = ['Teguran Lisan', 'Teguran Tertulis', 'SP1', 'SP2', 'SP3', 'Skorsing'];
const STATUS_PELANGGARAN = ['Aktif', 'Selesai', 'Dihapus'];

const statusConfig = {
  'Aktif': { bg: '#fee2e2', color: '#991b1b', dot: '#f5365c' },
  'Selesai': { bg: '#d1fae5', color: '#065f46', dot: '#2dce89' },
  'Dihapus': { bg: '#f3f4f6', color: '#6b7280', dot: '#8898aa' },
};

const jenisColor = {
  'Ringan': { bg: '#fef3c7', color: '#92400e' },
  'Sedang': { bg: '#eef9fd', color: '#0a66c2' },
  'Berat': { bg: '#fee2e2', color: '#991b1b' },
};

export default function HrisPelanggaran() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ karyawan_id: '', tgl_pelanggaran: '', jenis_pelanggaran: 'Ringan', keterangan: '', jenis_sanksi: '', tgl_sanksi: '' });

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisPelanggaran(), getKaryawan()])
      .then(([dRes, kRes]) => { setData(dRes.data); setKaryawan(kRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ karyawan_id: '', tgl_pelanggaran: '', jenis_pelanggaran: 'Ringan', keterangan: '', jenis_sanksi: '', tgl_sanksi: '' });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisPelanggaranById(id);
      const d = res.data;
      setEditing(d);
      setForm({ karyawan_id: d.karyawan_id, tgl_pelanggaran: d.tgl_pelanggaran, jenis_pelanggaran: d.jenis_pelanggaran,
        keterangan: d.keterangan, jenis_sanksi: d.jenis_sanksi, tgl_sanksi: d.tgl_sanksi || '', status: d.status });
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data'); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id || !form.tgl_pelanggaran) { alert('Lengkapi field wajib'); return; }
    try {
      if (editing) {
        await updateHrisPelanggaran(editing.id, { ...form, status: form.status || 'Aktif' });
      } else {
        await createHrisPelanggaran(form);
      }
      setShowModal(false);
      fetchData();
    } catch (e) { alert('Gagal menyimpan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data pelanggaran ini?')) return;
    try { await deleteHrisPelanggaran(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  const totalData = data.length;
  const totalAktif = data.filter(d => d.status === 'Aktif').length;
  const totalSelesai = data.filter(d => d.status === 'Selesai').length;

  const filtered = data.filter(d => {
    const matchSearch = !search || d.nama?.toLowerCase().includes(search.toLowerCase()) || d.jenis_pelanggaran?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <AlertTriangle size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: '#fb6340' }} />
          Data Pelanggaran & Sanksi
        </h2>
      </div>

      {/* Stat Cards */}
      {data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Pelanggaran</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalData}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #f5365c' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Aktif</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#991b1b', lineHeight: 1.2 }}>{totalAktif}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Selesai</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{totalSelesai}</div>
          </div>
        </div>
      )}

      {/* Toolbar: Tambah | Search | Status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Tambah Pelanggaran
        </button>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Status:</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="form-select"
            style={{ width: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
            <option value="">Semua</option>
            {STATUS_PELANGGARAN.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{totalData}
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data pelanggaran</div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {filtered.map(d => {
            const sc = statusConfig[d.status] || statusConfig['Aktif'];
            const jc = jenisColor[d.jenis_pelanggaran] || jenisColor['Ringan'];
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${sc.dot}` }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, #fb6340, #f5365c)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <AlertTriangle size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>{d.nama}</h3>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4,
                          background: jc.bg, color: jc.color, fontWeight: 600,
                        }}>{d.jenis_pelanggaran}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          background: sc.bg, color: sc.color,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot }} />
                          {d.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  <div className="region-detail"><Calendar size={14} className="text-muted" /><strong>Tanggal:</strong> {d.tgl_pelanggaran}</div>
                  <div className="region-detail"><Shield size={14} className="text-muted" /><strong>Sanksi:</strong> {d.jenis_sanksi || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Belum ditentukan</span>}</div>
                  {d.tgl_sanksi && <div className="region-detail"><Clock size={14} className="text-muted" /><strong>Tgl. Sanksi:</strong> {d.tgl_sanksi}</div>}
                  {d.keterangan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                      <strong style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Keterangan:</strong>
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem', lineHeight: 1.5 }}>{d.keterangan}</span>
                    </div>
                  )}
                </div>
                <div className="action-btns" style={{ padding: '10px 16px', borderTop: '1px solid var(--gray-100)', justifyContent: 'flex-end' }}>
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(d.id)}><Edit2 size={14} /></button>
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #fb6340 0%, #f5365c 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{editing ? 'Edit Pelanggaran' : 'Tambah Pelanggaran'}</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Section Header */}
                <div style={{
                  padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                  background: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--cyan-700)',
                  textAlign: 'center',
                }}>
                  <AlertTriangle size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Data Pelanggaran
                </div>

                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Karyawan <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis Pelanggaran <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.jenis_pelanggaran} onChange={e => setForm({...form, jenis_pelanggaran: e.target.value})}>
                      {JENIS_PELANGGARAN.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Pelanggaran <span style={{color:'var(--danger)'}}>*</span></label>
                    <input type="date" value={form.tgl_pelanggaran} onChange={e => setForm({...form, tgl_pelanggaran: e.target.value})} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis Sanksi</label>
                    <select value={form.jenis_sanksi} onChange={e => setForm({...form, jenis_sanksi: e.target.value})}>
                      <option value="">Pilih Sanksi</option>
                      {JENIS_SANKSI.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Sanksi</label>
                    <input type="date" value={form.tgl_sanksi} onChange={e => setForm({...form, tgl_sanksi: e.target.value})} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    {editing && (
                      <>
                        <label>Status</label>
                        <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                          {STATUS_PELANGGARAN.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ padding: '0 16px 14px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Keterangan <span style={{color:'var(--danger)'}}>*</span></label>
                    <textarea value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} rows={2} required style={{ fontSize: '0.82rem' }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSubmit}>{editing ? 'Simpan' : 'Tambah'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
